/**
 * ClicknGO — Centralised HTTP client
 * All requests include credentials (HTTP-only cookie session) and CSRF token.
 */

const BASE_URL = process.env.REACT_APP_API_URL || '/api';

// CSRF token cached from the last successful response header
let csrfToken = null;

// Exposed so the few components that issue raw fetch() writes (event wizard
// ticket-types, org logo upload) can attach the same CSRF token. The backend
// now enforces double-submit on all authenticated state-mutating requests.
export const getCsrfToken = () => csrfToken;

async function request(method, endpoint, data = null) {
  const headers = { 'Content-Type': 'application/json' };
  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  const options = {
    method,
    headers,
    credentials: 'include', // always send HTTP-only session cookie
  };

  if (data && method !== 'GET') {
    options.body = JSON.stringify(data);
  }

  const url = endpoint.startsWith('http') ? endpoint : `${BASE_URL}${endpoint}`;

  let response;
  try {
    response = await fetch(url, options);
  } catch (networkError) {
    throw { type: 'NETWORK_ERROR', message: 'Unable to reach server. Check your connection.' };
  }

  const contentType = response.headers.get('Content-Type') || '';
  const isJson = contentType.includes('application/json');
  const body = isJson ? await response.json() : await response.text();

  // Capture CSRF token — read from header first, fall back to body field.
  // cPanel/Apache can strip custom response headers, so the body field is
  // the reliable path; we keep the header read as a bonus.
  const newCsrf = response.headers.get('X-CSRF-Token')
    || (isJson && body && typeof body === 'object' ? body.csrf_token : null)
    || null;
  if (newCsrf) csrfToken = newCsrf;

  if (!response.ok) {
    // Session expired — bounce to login so user isn't silently stuck
    // Skip for /auth/me (used on page load to check if logged in — 401 is expected)
    if (response.status === 401
      && window.location.pathname !== '/login'
      && !endpoint.endsWith('/auth/me')
    ) {
      window.location.href = '/login?expired=1';
    }
    throw {
      type: 'API_ERROR',
      status: response.status,
      message: (isJson && body.error) ? body.error : `HTTP ${response.status}`,
      body,
    };
  }

  return body;
}

const http = {
  get:    (endpoint)        => request('GET',    endpoint),
  post:   (endpoint, data)  => request('POST',   endpoint, data),
  put:    (endpoint, data)  => request('PUT',    endpoint, data),
  delete: (endpoint)        => request('DELETE', endpoint),
};

// ─── add patch helper ────────────────────────────────────────────────────────
http.patch = (endpoint, data) => request('PATCH', endpoint, data);

// ─── multipart upload (file inputs) — omits Content-Type so browser sets boundary ─
http.upload = async (endpoint, formData) => {
  const headers = {};
  if (csrfToken) headers['X-CSRF-Token'] = csrfToken;

  const url = endpoint.startsWith('http') ? endpoint : `${BASE_URL}${endpoint}`;
  let response;
  try {
    response = await fetch(url, { method: 'POST', headers, credentials: 'include', body: formData });
  } catch {
    throw { type: 'NETWORK_ERROR', message: 'Unable to reach server. Check your connection.' };
  }

  const contentType = response.headers.get('Content-Type') || '';
  const isJson = contentType.includes('application/json');
  const body = isJson ? await response.json() : await response.text();

  const newCsrf = response.headers.get('X-CSRF-Token')
    || (isJson && body && typeof body === 'object' ? body.csrf_token : null)
    || null;
  if (newCsrf) csrfToken = newCsrf;

  if (!response.ok) {
    throw { type: 'API_ERROR', status: response.status, message: (isJson && body.error) ? body.error : `HTTP ${response.status}`, body };
  }
  return body;
};
// ─────────────────────────────────────────────────────────────────────────────

// Auth
export const authApi = {
  login:               (email, password)    => http.post('/auth/login', { email, password }),
  logout:              ()                   => http.post('/auth/logout'),
  me:                  ()                   => http.get('/auth/me'),
  changePassword:      (currentPwd, newPwd) => http.put('/auth/change-password', { current_password: currentPwd, new_password: newPwd }),
  completeOnboarding:  ()                   => http.patch('/auth/onboarding-complete'),
};

// Notifications
export const notificationsApi = {
  list:        (unreadOnly = false) => http.get(`/notifications${unreadOnly ? '?unread_only=1' : ''}`),
  markRead:    (id)                 => http.patch(`/notifications/${id}/read`),
  markAllRead: ()                   => http.post('/notifications/read-all'),
};

// Events
export const eventsApi = {
  list:        ()           => http.get('/events'),
  get:         (id)         => http.get(`/events/${id}`),
  getBySlug:   (slug)       => http.get(`/events/slug/${slug}`),
  create:      (data)       => http.post('/events', data),
  createDraft: (name)       => http.post('/events/draft', { name }),
  update:      (id, data)   => http.put(`/events/${id}`, data),
  publish:     (id)         => http.post(`/events/${id}/publish`),
  clone:       (id)         => http.post(`/events/${id}/clone`),
  delete:      (id)         => http.delete(`/events/${id}`),
  summary:     (id)         => http.get(`/events/${id}/summary`),
  scanStats:   (id)         => http.get(`/events/${id}/scan-stats`),
  revenue:     (id)         => http.get(`/events/${id}/revenue`),
};

// Public ticket lookup (no auth)
export const ticketApi = {
  get: (token) => http.get(`/ticket/${token}`),
};

// Entry tiers (ticket types)
export const tiersApi = {
  list:       (eventId)       => http.get(`/events/${eventId}/ticket-types`),
  listPublic: (eventId)       => http.get(`/events/${eventId}/ticket-types/public`),
  create:     (eventId, data) => http.post(`/events/${eventId}/ticket-types`, data),
  update:     (id, data)      => http.put(`/ticket-types/${id}`, data),
  delete:     (id)            => http.delete(`/ticket-types/${id}`),
  reorder:    (order)         => http.post('/ticket-types/reorder', { order }),
};

// Event form fields
export const formFieldsApi = {
  get:  (eventId)         => http.get(`/events/${eventId}/form-fields`),
  save: (eventId, fields) => http.post(`/events/${eventId}/form-fields`, { fields }),
};

// File uploads (multipart — bypasses JSON request helper)
export const uploadApi = {
  eventImage: (eventId, file, field) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('field', field);
    return fetch(`${BASE_URL}/events/${eventId}/upload`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    }).then(r => r.json());
  },
};

// Scanning
export const scanApi = {
  scan:        (qrToken, zoneId) => http.post('/scan', { qr_token: qrToken, zone_id: zoneId }),
  sponsorScan: (qrToken, zoneId, extras) =>
    http.post('/sponsor/scan', { qr_token: qrToken, zone_id: zoneId, ...extras }),
};

// Leads
export const leadsApi = {
  list: (eventId) => http.get(`/leads?event_id=${eventId}`),
};

// Wallet
export const walletApi = {
  balance:         ()            => http.get('/wallet/balance'),
  transactions:    (page = 1)   => http.get(`/wallet/transactions?page=${page}`),
  packages:        ()            => http.get('/wallet/packages'),
  featureCosts:    ()            => http.get('/wallet/feature-costs'),
  updateTopup:     (settings)   => http.post('/wallet/topup-settings', settings),
  forecast:        (eventId)    => http.get(`/wallet/forecast/${eventId}`),
  purchaseOrder:   (packageId)  => http.post('/wallet/purchase-order', { package_id: packageId }),
  purchaseConfirm: (data)       => http.post('/wallet/purchase-confirm', data),
};

// Refunds
export const refundApi = {
  refund: (registrationId, reason) => http.post(`/registrations/${registrationId}/refund`, { reason }),
};

// Analytics
export const analyticsApi = {
  get:      (eventId) => http.get(`/events/${eventId}/analytics`),
  revenue:  (eventId) => http.get(`/events/${eventId}/revenue`),
  scanStats:(eventId) => http.get(`/events/${eventId}/scan-stats`),
};

// Waitlist
export const waitlistApi = {
  join:        (eventId, data)    => http.post(`/events/${eventId}/waitlist`, data),
  list:        (eventId, status)  => http.get(`/events/${eventId}/waitlist${status ? `?status=${status}` : ''}`),
  leave:       (id, email)        => http.delete(`/waitlist/${id}?email=${encodeURIComponent(email)}`),
  promote:     (id, expiresHours) => http.post(`/waitlist/${id}/promote`, { expires_hours: expiresHours }),
  adminRemove: (id)               => http.delete(`/waitlist/${id}/admin`),
};

// Org settings
export const orgApi = {
  getSettings:    ()     => http.get('/org/settings'),
  updateSettings: (data) => http.put('/org/settings', data),
};

// Payments (Razorpay — paid ticket flow)
export const paymentApi = {
  verify: (data) => http.post('/payment/verify', data),
};

// Platform admin: token pricing (feature-level token costs)
export const pricingApi = {
  list:            ()                          => http.get('/admin/pricing'),
  setPrice:        (featureKey, tokenCost, effectiveFrom, note) =>
    http.post('/admin/pricing', { feature_key: featureKey, token_cost: tokenCost, effective_from: effectiveFrom || null, note }),
  cancelScheduled: (id)            => http.delete(`/admin/pricing/${id}`),
};

// Platform admin: token package management (INR bundle prices)
export const packagesAdminApi = {
  list:   ()           => http.get('/admin/packages'),
  create: (data)       => http.post('/admin/packages', data),
  update: (id, data)   => http.put(`/admin/packages/${id}`, data),
  delete: (id)         => http.delete(`/admin/packages/${id}`),
};

// Platform admin: global settings + org Route config
export const platformSettingsApi = {
  get:       ()          => http.get('/admin/platform-settings'),
  update:    (data)      => http.put('/admin/platform-settings', data),
  listOrgs:  ()          => http.get('/admin/platform-settings/orgs'),
  updateOrg: (id, data)  => http.put(`/admin/platform-settings/orgs/${id}`, data),
};

// Platform admin: platform-wide revenue (bifurcated)
export const platformRevenueApi = {
  get: (days = 30, orgId = 0) =>
    http.get(`/platform/revenue?days=${days}${orgId ? `&org_id=${orgId}` : ''}`),
};

// WhatsApp template library
export const whatsappTemplatesApi = {
  // Organiser: active templates of a type ('confirmation' | 'reminder')
  list:      (type)        => http.get(`/whatsapp-templates?type=${encodeURIComponent(type)}`),
  // Platform admin
  adminList: ()            => http.get('/admin/whatsapp-templates'),
  create:    (data)        => http.post('/admin/whatsapp-templates', data),
  update:    (id, data)    => http.put(`/admin/whatsapp-templates/${id}`, data),
  remove:    (id)          => http.delete(`/admin/whatsapp-templates/${id}`),
  test:      (id, phone)   => http.post(`/admin/whatsapp-templates/${id}/test`, { phone }),
};

// Bulk attendee import (CSV file upload)
export const importApi = {
  importCsv: (eventId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return http.upload(`/events/${eventId}/registrations/import`, fd);
  },
};

// QR Batch Export
export const qrExportApi = {
  export: (eventId) => http.get(`/events/${eventId}/qr-export`),
};

export default http;
